import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Megaphone, Users, Building2, Landmark } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { PageTransition } from "@/components/PageTransition";
import { AnimatedMeshGradient } from "@/components/AnimatedMeshGradient";
import { TiltGlowCard } from "@/components/TiltGlowCard";
import { MarketTape, TerminalGrid, useBoardTicks } from "@/components/MarketBoard";
import { usePrefersReducedMotion } from "@/lib/usePrefersReducedMotion";
import { useT } from "@/lib/i18n";
import { PARTNERS } from "@/constants/testIds";

// Ambient "living screen" intensity. Flip to "vivid" to compare the variants.
const BOARD_VARIANT = "sober"; // "sober" | "vivid"

// Static per-segment data; text comes from i18n. `metric` is an abstract index
// that ticks slowly (illustrative — not real data).
const SEG_META = [
  { icon: Briefcase, num: "01", testId: PARTNERS.ibs, metric: 1284 },
  { icon: Megaphone, num: "02", testId: PARTNERS.affiliates, metric: 318 },
  { icon: Users, num: "03", testId: PARTNERS.wealth, metric: 742 },
  { icon: Building2, num: "04", testId: PARTNERS.family, metric: 205 },
  { icon: Landmark, num: "05", testId: PARTNERS.institutional, metric: 1660 },
];

// Abstract indices for the slow market tape (ambient — not real quotes).
const TAPE = [
  { label: "LATAM·IB", value: "1,284" },
  { label: "EMEA", value: "318" },
  { label: "APAC", value: "742" },
  { label: "FLOW", value: "205" },
  { label: "INST", value: "1,660" },
  { label: "BRIDGE", value: "96.4" },
  { label: "DESK", value: "412" },
  { label: "ACCESS", value: "1,038" },
];

const SEEDS = SEG_META.map((m) => m.metric);

export default function Partners() {
  const t = useT();
  const SEGMENTS = SEG_META.map((m, i) => ({ ...m, ...t.partners.segments[i] }));
  const boardRef = useRef(null);
  const [active, setActive] = useState(true);
  const reduced = usePrefersReducedMotion();
  const vivid = BOARD_VARIANT === "vivid";

  // Pause all board motion when the section is off-screen (perf).
  useEffect(() => {
    const el = boardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(([e]) => setActive(e.isIntersecting), { threshold: 0.04 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const values = useBoardTicks(SEEDS, {
    active,
    reduced,
    intervalMs: vivid ? 2200 : 3300,
  });

  return (
    <PageTransition>
      {/* HERO */}
      <section data-testid={PARTNERS.intro} className="relative paper-grain overflow-hidden" style={{ background: "var(--nexxis-bg)" }}>
        <AnimatedMeshGradient intensity={1} />
        <div className="hero-fade-bottom" aria-hidden="true" />
        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 pt-36 sm:pt-44 pb-16">
          <Reveal><div className="kicker mb-6">{t.partners.heroKicker}</div></Reveal>
          <div className="grid grid-cols-12 gap-8 items-end">
            <Reveal as="div" className="col-span-12 lg:col-span-8">
              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(40px, 5.5vw, 76px)",
                  lineHeight: 1.05,
                  letterSpacing: "-0.02em",
                  color: "var(--nexxis-ink)",
                  fontWeight: 500,
                }}
              >
                {t.partners.heroTitle}
              </h1>
            </Reveal>
            <Reveal as="div" delay={0.08} className="col-span-12 lg:col-span-4">
              <div className="h-px mb-5" style={{ background: "#FF6857", width: 48 }} />
              <p className="font-ui" style={{ color: "var(--nexxis-ink-muted)", lineHeight: 1.7, fontSize: 15 }}>
                {t.partners.heroBody}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SEGMENTS — a "living trading screen" board */}
      <section
        ref={boardRef}
        className={`relative overflow-hidden ${active ? "" : "nxx-board-paused"}`}
        style={{ background: "var(--nexxis-bg-alt)" }}
      >
        {/* terminal grid across the whole section */}
        <TerminalGrid opacity={vivid ? 1 : 0.65} />
        {/* soft "observed panel" vignette */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{ background: "radial-gradient(ellipse 92% 82% at 50% 38%, transparent 55%, rgba(6,32,31,0.12))" }}
        />

        {/* slow market tape */}
        <div className="relative border-y" style={{ borderColor: "var(--nexxis-border)" }}>
          <MarketTape items={TAPE} duration={vivid ? 74 : 104} className="py-2.5" />
        </div>

        <div className="relative z-10 mx-auto max-w-[1200px] px-5 sm:px-8 py-20 sm:py-24">
          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-8" style={{ perspective: "1200px" }}>
            {SEGMENTS.map((s, idx) => (
              <StaggerItem key={s.testId}>
                <TiltGlowCard
                  testId={s.testId}
                  icon={s.icon}
                  num={s.num}
                  title={s.title}
                  body={s.body}
                  notes={s.notes}
                  to="/contact"
                  ctaLabel={t.partners.cardCta}
                  lineSeed={idx + 1}
                  value={values[idx]}
                  metricLabel="30D"
                  variant={BOARD_VARIANT}
                />
              </StaggerItem>
            ))}
          </Stagger>

          <div className="mt-14">
            <Reveal>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 rounded-[10px] px-5 py-3 font-ui text-[13px]"
                style={{ background: "#0D5F5E", color: "#F7F6F2", letterSpacing: "0.04em", fontWeight: 600 }}
              >
                <span>{t.partners.ctaLink}</span>
                <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </PageTransition>
  );
}
